/**
 * API Client Configuration for React Query (Patient App)
 *
 * This file configures the @hey-api generated clients to include
 * all necessary headers and interceptors that mirror the behavior
 * of the existing HttpService.
 */

import { applicationLocalStorage } from '@application/application_storage_local';
import { parseCookies } from '@cortex-shim/parse_cookies';
import { client as clientV2 } from '@visiba/api-vcs-client-v2';
import { client as generalClient } from '@visiba/api-vcs-general';
import { client as guestClient } from '@visiba/api-vcs-guest';

/**
 * Get web version from localStorage
 */
function getWebVersion(): string | null {
	try {
		return applicationLocalStorage.get('webVersion');
	} catch {
		return null;
	}
}

/**
 * Get the current language code from document or localStorage
 */
function getLangCode(): string | null {
	return document.documentElement.lang || null;
}

/**
 * Configure request interceptor for a client
 */
function configureRequestInterceptor(client: typeof clientV2 | typeof generalClient | typeof guestClient): void {
	client.interceptors.request.use((request, _options) => {
		const headers = new Headers(request.headers);

		// Default headers (matching HttpService)
		headers.set('Content-Type', 'application/json');
		headers.set('Pragma', 'no-cache');
		headers.set('If-Modified-Since', 'Mon, 26 Jul 1997 05:00:00 GMT');
		headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');

		// Add CSRF token
		const csrfToken = parseCookies(document.cookie).get('XSRF-TOKEN');
		if (csrfToken) {
			headers.set('X-XSRF-TOKEN', csrfToken);
		}

		// Add web version header
		const webVersion = getWebVersion();
		if (webVersion) {
			headers.set('X-App-WebVersion', webVersion);
		}

		// Add language header
		const langCode = getLangCode();
		if (langCode) {
			headers.set('X-App-Lang', langCode);
		}

		// Create new request with updated headers
		return new Request(request, { headers });
	});
}

/**
 * Configure response interceptor for a client
 */
function configureResponseInterceptor(client: typeof clientV2 | typeof generalClient | typeof guestClient): void {
	client.interceptors.response.use((response, _request, _options) => {
		// Handle X-App-WebVersion header
		const webVersion = response.headers.get('X-App-WebVersion');
		if (webVersion) {
			try {
				applicationLocalStorage.set('webVersion', webVersion);
			} catch (error) {
				console.error('Failed to set web version:', error);
			}
		}

		return response;
	});
}

let configured = false;

/**
 * Configure all API clients with request and response interceptors.
 * Safe to call multiple times — interceptors are only registered once.
 */
export function configureApiClients(): void {
	if (configured) return;
	configured = true;

	// Configure client-v2
	configureRequestInterceptor(clientV2);
	configureResponseInterceptor(clientV2);

	// Configure general client
	configureRequestInterceptor(generalClient);
	configureResponseInterceptor(generalClient);

	// Configure guest client
	configureRequestInterceptor(guestClient);
	configureResponseInterceptor(guestClient);
}
